import React from 'react';
import { useTranslation } from 'react-i18next';
import { getTeamLogoUrl } from '../../utils/formatters';
import './TeamLogo.css';

/**
 * Small inline team emblem used wherever a league team is listed (standings,
 * fixtures, match header). Renders the uploaded logo when present; otherwise an
 * optional lettered fallback circle (opt-in via ``fallback`` so individual/duel
 * competitors, which have no team, render nothing instead of a stray initial).
 *
 * @param {object} props - Component props.
 * @param {string} [props.logoUrl] - Team ``logo_url`` from the API.
 * @param {string} [props.name] - Team name (alt text + fallback initial).
 * @param {string} [props.tag] - Team tag (preferred for the fallback initial).
 * @param {boolean} [props.fallback=false] - Show a lettered circle when no logo.
 * @param {string} [props.className='team-logo-sm'] - Size/modifier class.
 * @returns {JSX.Element|null} The emblem element, or null when nothing to show.
 */
export default function TeamLogo({ logoUrl, name, tag, fallback = false, className = 'team-logo-sm' }) {
  const { t } = useTranslation();
  const url = getTeamLogoUrl(logoUrl);
  if (url) {
    return <img className={`team-logo ${className}`} src={url} alt={name ? t('leagues.teamEmblemAlt', { name }) : ''} />;
  }
  if (!fallback) return null;
  const initial = (tag || name || '?').trim()[0] || '?';
  return (
    <span className={`team-logo team-logo-blank ${className}`} aria-hidden="true">{initial}</span>
  );
}
